<template>
  <BaseLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1 d-flex align-items-center gap-2">
            <span>{{ site?.name || 'Site' }}</span>
            <span v-if="site && site.code" class="badge bg-light text-muted">
              {{ site.code }}
            </span>
            <span v-if="site" class="badge bg-primary-subtle text-primary border">
              {{ siteTypeLabel(site.site_type) }}
            </span>
          </h1>

          <div class="d-flex flex-wrap align-items-center gap-2">
            <span v-if="site" class="badge" :class="site.is_active ? 'bg-success' : 'bg-secondary'">
              {{ site.is_active ? 'Actif' : 'Inactif' }}
            </span>

            <span v-if="site && site.timezone" class="badge bg-light text-muted">
              {{ site.timezone }}
            </span>

            <span v-if="site && site.city" class="badge bg-light text-muted">
              {{ site.city }} <span v-if="site.country">— {{ site.country }}</span>
            </span>
          </div>
        </div>

        <div class="d-flex flex-column align-items-end gap-2">
          <div class="btn-group btn-group-sm">
            <button type="button" class="btn btn-outline-secondary btn-icon-label" @click="goBack" title="Retour à la liste des sites">
              <i class="fa fa-arrow-left"></i>
              <span class="btn-label ms-1">Retour à la liste des sites</span>
            </button>

            <button
              v-if="site"
              type="button"
              class="btn btn-outline-primary btn-icon-label"
              @click="openEditDialog"
              :disabled="isLoading"
              title="Modifier"
            >
              <i class="fa fa-pen"></i>
              <span class="btn-label ms-1">Modifier</span>
            </button>

            <button
              v-if="site && site.is_active"
              type="button"
              class="btn btn-outline-warning btn-icon-label"
              @click="deactivateSite"
              :disabled="isLoading"
              title="Désactiver"
            >
              <i class="fa fa-ban"></i>
              <span class="btn-label ms-1">Désactiver</span>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-outline-success btn-icon-label"
              @click="activateSite"
              :disabled="isLoading"
              title="Activer"
            >
              <i class="fa fa-check"></i>
              <span class="btn-label ms-1">Activer</span>
            </button>

            <AppLoadingButton
              type="button"
              class="btn btn-outline-danger btn-icon-label"
              :loading="isDeleting"
              loading-label="Suppression..."
              @click="deleteSite"
              :disabled="isLoading"
              title="Supprimer"
            >
              <i class="fa fa-trash"></i>
              <span class="btn-label ms-1">Supprimer</span>
            </AppLoadingButton>
          </div>

          <!-- Bouton pour basculer sur ce site -->
          <button
            v-if="site && site.id && isGerant"
            type="button"
            class="btn btn-sm btn-primary btn-icon-label"
            @click="switchToThisSite"
            :disabled="isLoading || isCurrentSite"
            title="Se connecter à ce site"
          >
            <i class="fa fa-exchange-alt"></i>
            <span class="btn-label ms-1" v-if="isCurrentSite">Site actuel</span>
            <span class="btn-label ms-1" v-else>Se connecter à ce site</span>
          </button>
        </div>
      </div>

      <!-- Messages -->
      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <!-- Détails -->
      <div class="row g-3 align-items-start" v-if="site">
        <div class="col-lg-6">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-light">
              <strong>Informations générales</strong>
            </div>
            <div class="card-body small">
              <div class="mb-3">
                <div class="text-muted">Image de couverture</div>
                <div v-if="site.cover_image_url" class="site-cover-preview mt-1">
                  <img :src="site.cover_image_url" :alt="`Couverture ${site.name || 'site'}`" />
                </div>
                <div v-else class="site-cover-preview site-cover-preview-empty mt-1">
                  {{ siteInitials(site) }}
                </div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Nom commercial</div>
                <div>{{ site.name || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Type de site</div>
                <div>{{ siteTypeLabel(site.site_type) }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Raison sociale</div>
                <div>{{ site.legal_name || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Code</div>
                <div>{{ site.code || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Devise</div>
                <div>{{ site.currency_code || 'FCFA' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Logo (URL)</div>
                <div class="text-truncate">{{ site.logo_url || '-' }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-light">
              <strong>Coordonnées & Localisation</strong>
            </div>
            <div class="card-body small">
              <div class="mb-2">
                <div class="text-muted">Téléphone</div>
                <div>{{ site.phone || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">WhatsApp</div>
                <div>{{ site.whatsapp_phone || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Email</div>
                <div>{{ site.email || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Adresse</div>
                <div>{{ site.address || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Ville</div>
                <div>{{ site.city || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Pays</div>
                <div>{{ site.country || '-' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- TVA / CSS / Fuseau / Légal -->
        <div class="col-lg-6">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-light">
              <strong>Paramètres fiscaux & fuseau horaire</strong>
            </div>
            <div class="card-body small">
              <div class="mb-2">
                <div class="text-muted">TVA (%)</div>
                <div>{{ site.tva_rate != null ? site.tva_rate : '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">CSS (%)</div>
                <div>{{ site.css_rate != null ? site.css_rate : '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Fuseau horaire</div>
                <div>{{ site.timezone || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Statut configuration</div>
                <span
                  class="badge"
                  :class="site.is_complete ? 'bg-success' : 'bg-warning text-dark'"
                >
                  {{ site.is_complete ? 'Complète' : 'Incomplète' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Infos légales & footer -->
        <div class="col-lg-6">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-light">
              <strong>Infos légales & pied de page</strong>
            </div>
            <div class="card-body small">
              <div class="mb-2">
                <div class="text-muted">N° contribuable / NIF</div>
                <div>{{ site.tax_id || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Registre de commerce</div>
                <div>{{ site.trade_register || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="text-muted">Texte de pied de page</div>
                <div style="white-space: pre-line;">
                  {{ site.footer_note || '-' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Si pas encore chargé -->
      <div v-else-if="isLoading" class="text-center py-5 text-muted">
        Chargement des informations du site...
      </div>

      <div v-else class="alert alert-warning py-2">
        Site introuvable.
      </div>

      <!-- MODAL FORMULAIRE -->
      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :style="{ width: '720px', maxWidth: '100%' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Modifier le site</h5>
        </template>

        <SiteForm
          mode="edit"
          :site="site"
          @saved="handleSaved"
          @cancel="closeDialog"
        />
      </PrimevueDialog>
    </template>
  </BaseLayout>
</template>

<script>
import SiteForm from './SiteForm.vue';

export default {
  name: 'SiteShowView',
  components: { SiteForm },
  data() {
    return {
      site: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      dialogVisible: false,
      isDeleting: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    isGerant() {
      return this.currentUser && this.currentUser.role === 'gerant';
    },
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    isCurrentSite() {
      return this.site && this.currentShopId && this.site.id === this.currentShopId;
    },
  },
  mounted() {
    this.fetchSite();
  },
  methods: {
    async fetchSite() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const id = this.$route.params.id;

      try {
        const res = await this.$api.get(`/shops/${id}`);
        this.site = res.data.data || res.data;
      } catch (error) {
        console.error('Erreur chargement site', error);
        this.errorMessage = 'Impossible de charger les informations du site.';
        this.site = null;
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'Sites' });
    },

    openEditDialog() {
      if (!this.site) return;
      this.dialogVisible = true;
    },

    siteTypeLabel(value) {
      const labels = {
        bars: 'Bars',
        epicerie: 'Épicerie',
        restaurant: 'Restaurant',
        boutique_en_ligne: 'Boutique en ligne',
        autres: 'Autres',
      };

      return labels[value] || 'Épicerie';
    },

    siteInitials(site) {
      const source = String(site?.name || site?.code || 'Site').trim();
      const words = source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);

      return (words.length ? words.map((word) => word[0]).join('') : 'ST').toUpperCase();
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    async handleSaved() {
      this.dialogVisible = false;
      await this.fetchSite();

      if (this.site && this.currentShopId && Number(this.site.id) === Number(this.currentShopId)) {
        await this.$store.dispatch('shop/fetchInfo');
      }

      this.successMessage = 'Site enregistré avec succès.';
    },

    async activateSite() {
      if (!this.site || !this.site.id) return;
      const ok = await this.$confirmAction(`Activer le site "${this.site.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        await this.$api.patch(`/shops/${this.site.id}/activate`);
        await this.fetchSite();
        this.successMessage = 'Site activé avec succès.';
      } catch (error) {
        console.error('Erreur activation site', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de l’activation.';
      } finally {
        this.isLoading = false;
      }
    },

    async deactivateSite() {
      if (!this.site || !this.site.id) return;
      const ok = await this.$confirmAction(`Désactiver le site "${this.site.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        await this.$api.patch(`/shops/${this.site.id}/deactivate`);
        await this.fetchSite();
        this.successMessage = 'Site désactivé avec succès.';
      } catch (error) {
        console.error('Erreur désactivation site', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la désactivation.';
      } finally {
        this.isLoading = false;
      }
    },

    async deleteSite() {
      if (!this.site || !this.site.id) return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction(
        `Supprimer le site "${this.site.name}" ? Cette opération est définitive et possible uniquement si le site est vide.`
      );
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        await this.$api.delete(`/shops/${this.site.id}`);
        this.successMessage = 'Site supprimé avec succès.';
        this.$router.push({ name: 'Sites' });
      } catch (error) {
        console.error('Erreur suppression site', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la suppression.';
      } finally {
        this.isLoading = false;
        this.isDeleting = false;
      }
    },

    async switchToThisSite() {
      if (!this.site || !this.site.id) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isLoading = true;

      try {
        await this.$store.dispatch('shop/changeSite', this.site.id);
        this.successMessage = 'Vous êtes maintenant connecté sur ce site.';
        this.$router.push({ name: 'Dashboard' });
      } catch (error) {
        console.error('Erreur changement de site', error);
        this.errorMessage = 'Impossible de basculer sur ce site.';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.site-cover-preview {
  align-items: center;
  background: linear-gradient(135deg, #2f6f4e, #d7a431);
  border-radius: 0.5rem;
  color: #fff;
  display: flex;
  font-size: 2rem;
  font-weight: 800;
  height: 132px;
  justify-content: center;
  overflow: hidden;
  width: 100%;
}

.site-cover-preview img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.site-cover-preview-empty {
  letter-spacing: 0;
}
</style>
